import { create } from 'zustand';
import { Howl } from 'howler';
import { useAuthStore } from './authStore';
import { useListenStore } from './listenStore';
import type { SongSummary } from '../types';

interface AudioState {
  // Current playback
  currentSong: SongSummary | null;
  howl: Howl | null;
  isPlaying: boolean;
  isPinned: boolean; // Pinned songs don't stop on hover-away
  volume: number;
  seek: number;
  duration: number;

  // Playlist state
  playlist: SongSummary[];
  playlistIndex: number;
  isPlaylistMode: boolean;

  // Visualizer
  analyser: AnalyserNode | null;
  audioContext: AudioContext | null;

  // Actions
  playSong: (song: SongSummary, pinned?: boolean, fromPlaylist?: boolean) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  togglePlayPause: () => void;
  setVolume: (volume: number) => void;
  setSeek: (seek: number) => void;
  setPinned: (pinned: boolean) => void;

  // Playlist actions
  playPlaylist: (songs: SongSummary[]) => void;
  playFromCurrent: (songs: SongSummary[]) => void;
  stopPlaylist: () => void;
  advancePlaylist: () => void;

  // Internal
  updateSeek: () => void;
  cleanup: () => void;
  cleanupFadingHowls: () => void;
  getCrossfadeDuration: () => number;
}

const DEFAULT_CROSSFADE_DURATION = 500; // 500ms default

let seekInterval: number | null = null;
let fadingOutHowls: Howl[] = []; // Track Howls being faded out for cleanup

export const useAudioStore = create<AudioState>((set, get) => ({
  currentSong: null,
  howl: null,
  isPlaying: false,
  isPinned: false,
  volume: useAuthStore.getState().user?.preferences?.audioVolume ?? 0.7,
  seek: 0,
  duration: 0,
  playlist: [],
  playlistIndex: -1,
  isPlaylistMode: false,
  analyser: null,
  audioContext: null,

  playSong: (song: SongSummary, pinned = false, fromPlaylist = false) => {
    const state = get();

    // Exit playlist mode if this is a manual play (not from playlist auto-advance)
    if (!fromPlaylist && state.isPlaylistMode) {
      console.log('[Playlist] Manual play detected, exiting playlist mode');
      set({ isPlaylistMode: false, playlist: [], playlistIndex: -1 });
    }

    // If same song is already set as current song
    if (state.currentSong?.id === song.id && state.howl) {
      console.log('[Crossfade] Same song clicked, isPlaying:', state.isPlaying, 'howl.playing():', state.howl.playing());

      // Check if it's truly paused (volume > 0 means it was playing before)
      const isPaused = !state.isPlaying && !state.howl.playing() && state.howl.volume() > 0;

      if (isPaused) {
        // Song was genuinely paused by user - resume it
        console.log('[Crossfade] Resuming paused track');
        state.howl.play();
        set({ isPlaying: true, isPinned: pinned });
      } else {
        // Song is either loading, playing, or was never started - don't call play()
        console.log('[Crossfade] Track already active, ignoring duplicate click. Volume:', state.howl.volume());
        // Just update pinned state if already playing
        if (state.isPlaying) {
          set({ isPinned: pinned });
        }
      }
      return;
    }

    // Handle crossfade if there's a currently playing song
    const oldHowl = state.howl;
    const targetVolume = state.volume;
    const crossfadeDuration = state.getCrossfadeDuration();
    const oldHowlWasPlaying = state.isPlaying; // Capture old playing state

    const transitionStartTime = Date.now();
    console.log('[Crossfade] Starting transition:', {
      crossfadeDuration,
      targetVolume,
      hasOldHowl: !!oldHowl,
      oldHowlWasPlaying,
      oldVolume: oldHowl?.volume(),
      time: transitionStartTime,
    });

    // Don't fade out the old track yet - wait for new track to start playing
    // We'll start the fade-out in the new track's onplay callback
    let oldTrackFadedOut = false;

    if (oldHowl && !oldHowlWasPlaying) {
      // If old track isn't playing, just clean up immediately
      console.log('[Crossfade] Old track not playing, immediate cleanup');
      oldHowl.stop();
      oldHowl.unload();
    } else if (oldHowl && crossfadeDuration === 0) {
      // If no crossfade, stop old track immediately
      console.log('[Crossfade] No crossfade (0ms), immediate cleanup');
      oldHowl.stop();
      oldHowl.unload();
    }
    // If oldHowl exists and is playing and we have crossfade > 0, keep it playing
    // and we'll fade it out when the new track starts

    // Clean up any stale fading howls (in case of rapid clicks)
    state.cleanupFadingHowls();

    // Use HTML5 audio for long files (>10 min) to avoid memory issues
    // Web Audio API decodes entire file into memory, HTML5 streams it
    const isLongFile = (song.durationSeconds ?? 0) > 600; // 10 minutes threshold
    const useHtml5 = isLongFile;

    // Create new Howl instance
    const howl = new Howl({
      src: [song.audioPath],
      html5: useHtml5, // HTML5 for long files (streaming), Web Audio for short (better fades)
      volume: crossfadeDuration > 0 ? 0 : targetVolume, // Start at 0 for fade-in, or target volume for instant
      onplay: () => {
        const playDelay = Date.now() - transitionStartTime;
        console.log('[Crossfade] New track started playing at volume', howl.volume(), 'after', playDelay, 'ms delay');

        // Only process if this is still the current Howl (prevents duplicate onplay callbacks)
        const currentState = get();
        if (currentState.howl !== howl) {
          console.log('[Crossfade] Ignoring onplay from old Howl instance');
          return;
        }

        set({ isPlaying: true, duration: howl.duration() });

        // Mark song as listened (if feature is enabled)
        const user = useAuthStore.getState().user;
        if (user?.preferences?.listenHistoryEnabled !== false) {
          useListenStore.getState().markAsListened(song);
        }

        // NOW that new track is playing, start crossfade
        if (crossfadeDuration > 0) {
          // Start fading in the new track
          console.log('[Crossfade] NOW fading in new track from', howl.volume(), 'to', targetVolume, 'over', crossfadeDuration, 'ms');
          howl.fade(howl.volume(), targetVolume, crossfadeDuration);

          // Start fading out the old track (if it exists and was playing)
          if (oldHowl && oldHowlWasPlaying && !oldTrackFadedOut) {
            const currentVolume = oldHowl.volume();
            console.log('[Crossfade] NOW fading out old track from', currentVolume, 'to 0 over', crossfadeDuration, 'ms');
            oldHowl.fade(currentVolume, 0, crossfadeDuration);
            oldTrackFadedOut = true;

            // Add to fading out list for cleanup
            fadingOutHowls.push(oldHowl);

            // Clean up old howl after fade completes
            setTimeout(() => {
              console.log('[Crossfade] Cleaning up old track');
              oldHowl.stop();
              oldHowl.unload();
              // Remove from fading list
              fadingOutHowls = fadingOutHowls.filter(h => h !== oldHowl);
            }, crossfadeDuration + 100); // Add 100ms buffer
          }
        }

        // Start seek interval
        if (seekInterval) clearInterval(seekInterval);
        seekInterval = window.setInterval(() => {
          get().updateSeek();
        }, 100);

        // TODO: Set up Web Audio API for visualizer
        // Web Audio API connection with Howler requires accessing internal nodes
        // For now, visualizer is disabled until we implement a custom audio solution
      },
      onpause: () => {
        if (get().howl !== howl) return; // Ignore if not current
        set({ isPlaying: false });
        if (seekInterval) {
          window.clearInterval(seekInterval);
          seekInterval = null;
        }
      },
      onstop: () => {
        if (get().howl !== howl) return; // Ignore if not current
        set({ isPlaying: false, seek: 0 });
        if (seekInterval) {
          window.clearInterval(seekInterval);
          seekInterval = null;
        }
      },
      onend: () => {
        if (get().howl !== howl) return; // Ignore if not current
        set({ isPlaying: false, seek: 0 });
        if (seekInterval) {
          window.clearInterval(seekInterval);
          seekInterval = null;
        }

        // Auto-advance to next song if in playlist mode
        const currentState = get();
        if (currentState.isPlaylistMode) {
          console.log('[Playlist] Song ended, advancing to next');
          currentState.advancePlaylist();
        }
      },
      onfade: () => {
        // Fade can complete on old howls during crossfade, so don't check instance
        console.log('[Crossfade] Fade completed on:', song.title);
      },
    });

    // Just play - fade will start in onplay callback
    howl.play();

    set({ currentSong: song, howl, isPinned: pinned });
  },

  pause: () => {
    const { howl } = get();
    if (howl) {
      howl.pause();
    }
  },

  resume: () => {
    const { howl } = get();
    if (howl) {
      howl.play();
    }
  },

  stop: () => {
    const { howl } = get();
    if (howl) {
      howl.stop();
    }
    set({ currentSong: null, isPinned: false, seek: 0 });
  },

  togglePlayPause: () => {
    const { isPlaying, howl } = get();
    if (howl) {
      if (isPlaying) {
        howl.pause();
      } else {
        howl.play();
      }
    }
  },

  setVolume: (volume: number) => {
    const { howl } = get();
    if (howl) {
      howl.volume(volume);
    }
    set({ volume });
  },

  setSeek: (seek: number) => {
    const { howl } = get();
    if (howl) {
      howl.seek(seek);
      set({ seek });
    }
  },

  setPinned: (pinned: boolean) => {
    set({ isPinned: pinned });
  },

  playPlaylist: (songs: SongSummary[]) => {
    if (songs.length === 0) {
      console.log('[Playlist] Cannot play empty playlist');
      return;
    }

    console.log('[Playlist] Starting playlist with', songs.length, 'songs');
    set({
      playlist: songs,
      playlistIndex: 0,
      isPlaylistMode: true,
    });

    // Start playing the first song with fromPlaylist flag
    get().playSong(songs[0], true, true);
  },

  playFromCurrent: (songs: SongSummary[]) => {
    if (songs.length === 0) {
      console.log('[Playlist] Cannot play empty playlist');
      return;
    }

    const state = get();
    let startIndex = 0;

    // If there's a current song, find its index in the songs array
    if (state.currentSong) {
      const currentIndex = songs.findIndex(song => song.id === state.currentSong?.id);
      if (currentIndex !== -1) {
        startIndex = currentIndex;
        console.log('[Playlist] Starting playlist from current song at index', startIndex);
      } else {
        console.log('[Playlist] Current song not found in subset, starting from beginning');
      }
    } else {
      console.log('[Playlist] No current song, starting from beginning');
    }

    console.log('[Playlist] Starting playlist from index', startIndex, 'with', songs.length, 'songs');
    set({
      playlist: songs,
      playlistIndex: startIndex,
      isPlaylistMode: true,
    });

    // Start playing from the determined index with fromPlaylist flag
    get().playSong(songs[startIndex], true, true);
  },

  stopPlaylist: () => {
    console.log('[Playlist] Stopping playlist');
    const state = get();

    // Stop current playback
    state.stop();

    // Clear playlist state
    set({
      playlist: [],
      playlistIndex: -1,
      isPlaylistMode: false,
    });
  },

  advancePlaylist: () => {
    const state = get();

    if (!state.isPlaylistMode || state.playlist.length === 0) {
      console.log('[Playlist] Not in playlist mode, cannot advance');
      return;
    }

    const nextIndex = state.playlistIndex + 1;

    if (nextIndex < state.playlist.length) {
      console.log('[Playlist] Advancing to track', nextIndex + 1, 'of', state.playlist.length);
      set({ playlistIndex: nextIndex });
      get().playSong(state.playlist[nextIndex], true, true);
    } else {
      console.log('[Playlist] Reached end of playlist, stopping');
      get().stopPlaylist();
    }
  },

  updateSeek: () => {
    const { howl } = get();
    if (howl && howl.playing()) {
      set({ seek: howl.seek() as number });
    }
  },

  cleanup: () => {
    const { howl } = get();
    if (howl) {
      howl.unload();
    }
    if (seekInterval) {
      window.clearInterval(seekInterval);
      seekInterval = null;
    }
    // Clean up any fading howls
    get().cleanupFadingHowls();
  },

  cleanupFadingHowls: () => {
    // Stop and unload all fading howls (useful for rapid song changes)
    fadingOutHowls.forEach(howl => {
      try {
        howl.stop();
        howl.unload();
      } catch (e) {
        // Ignore errors if howl already cleaned up
      }
    });
    fadingOutHowls = [];
  },

  getCrossfadeDuration: () => {
    // Get crossfade duration from user preferences
    const user = useAuthStore.getState().user;
    return user?.preferences?.crossfadeDurationMs ?? DEFAULT_CROSSFADE_DURATION;
  },
}));

// Cleanup on window unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    useAudioStore.getState().cleanup();
  });
}
